import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  useTheme,
  alpha,
  Stack,
  Paper,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Rating,
  Avatar,
  Tabs,
  Tab
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import VerifiedIcon from '@mui/icons-material/Verified';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GavelIcon from '@mui/icons-material/Gavel';
import DiamondIcon from '@mui/icons-material/Diamond';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import SchoolIcon from '@mui/icons-material/School';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Styled Components
const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: '#FFFFFF',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  borderRadius: 2,
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
    borderColor: theme.palette.primary.main,
  },
}));

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 4 }}>{children}</Box>}
  </div>
);

const Services = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Service Categories
  const services = [
    {
      id: 1,
      category: "Authentication",
      icon: <VerifiedIcon sx={{ fontSize: 40 }} />,
      title: "Professional Authentication",
      description: "Expert verification by leading numismatic authorities",
      features: [
        "PCGS & NGC Certification",
        "In-house Expert Review",
        "Die Variety Attribution",
        "Counterfeit Detection",
        "Provenance Research",
        "Conservation Assessment"
      ],
      price: "From $75",
      turnaround: "3-5 days",
    },
    {
      id: 2,
      category: "Valuation",
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      title: "Comprehensive Valuation",
      description: "Accurate market-based appraisals for all purposes",
      features: [
        "Insurance Appraisals",
        "Estate Valuation",
        "Fair Market Value",
        "Liquidation Value",
        "Collection Portfolio Review",
        "Market Trend Analysis"
      ],
      price: "From $150",
      turnaround: "5-7 days",
    },
    {
      id: 3,
      category: "Conservation",
      icon: <DiamondIcon sx={{ fontSize: 40 }} />,
      title: "Conservation & Preservation",
      description: "Professional conservation to maintain coin integrity",
      features: [
        "Surface Cleaning",
        "Stabilization",
        "Environmental Storage",
        "Capsule Replacement",
        "Humidity Control",
        "Long-term Preservation"
      ],
      price: "From $95",
      turnaround: "7-10 days",
    },
    {
      id: 4,
      category: "Selling",
      icon: <GavelIcon sx={{ fontSize: 40 }} />,
      title: "Private Treaty Sales",
      description: "Discreet sales to qualified collectors and institutions",
      features: [
        "Private Placement",
        "Auction Representation",
        "Direct Purchase",
        "Consignment Options",
        "Global Buyer Network",
        "Confidential Marketing"
      ],
      price: "Commission-based",
      turnaround: "Varies",
    },
    {
      id: 5,
      category: "Shipping",
      icon: <LocalShippingIcon sx={{ fontSize: 40 }} />,
      title: "Secure Shipping",
      description: "Fully insured, discreet transportation worldwide",
      features: [
        "Brinks Secure Transport",
        "Fully Insured",
        "Discreet Packaging",
        "Real-time Tracking",
        "Signature Confirmation",
        "International Logistics"
      ],
      price: "From $50",
      turnaround: "1-3 days",
    },
    {
      id: 6,
      category: "Investment",
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: "Investment Advisory",
      description: "Strategic guidance for building valuable collections",
      features: [
        "Portfolio Strategy",
        "Market Analysis",
        "Acquisition Sourcing",
        "Divestment Planning",
        "Tax Efficiency",
        "Wealth Preservation"
      ],
      price: "Custom Quote",
      turnaround: "Consultation",
    },
  ];

  // Authentication Services
  const authenticationServices = [
    {
      name: "PCGS Authentication",
      description: "World's leading coin grading service",
      price: "$75 - $250",
      time: "5-10 days",
      trusted: true,
    },
    {
      name: "NGC Certification",
      description: "Numismatic Guaranty Corporation",
      price: "$70 - $225",
      time: "5-10 days",
      trusted: true,
    },
    {
      name: "In-house Expert Review",
      description: "Our numismatists provide preliminary assessment",
      price: "$50",
      time: "2-3 days",
      trusted: true,
    },
    {
      name: "Provenance Research",
      description: "Historical ownership documentation",
      price: "$200 - $500",
      time: "10-14 days",
      trusted: true,
    },
  ];

  // Testimonials
  const testimonials = [
    {
      name: "James H.",
      role: "Private Collector",
      avatar: "JH",
      rating: 5,
      comment: "The authentication service provided exceptional detail. Their expertise gave me confidence in my acquisition.",
    },
    {
      name: "Elizabeth W.",
      role: "Estate Executor",
      avatar: "EW",
      rating: 5,
      comment: "The valuation report was comprehensive and accepted by the IRS without question. Excellent service.",
    },
    {
      name: "Robert M.",
      role: "Investment Advisor",
      avatar: "RM",
      rating: 5,
      comment: "Their investment advisory team helped my client build a diversified portfolio of rare coins.",
    },
  ];

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 }, 
      backgroundColor: '#F5F5F5', 
      minHeight: '100vh' 
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'primary.main',
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                fontWeight: 400,
                mb: 2
              }}
            >
              Comprehensive Numismatic Services
            </Typography>
            
            <Typography 
              variant="h2" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                color: '#2D2D2D',
                fontSize: { xs: '2.5rem', md: '4rem' }
              }}
            >
              Expert Solutions
              <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                for Collectors & Investors
              </Box>
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                color: '#616161',
                fontSize: '1.1rem',
                lineHeight: 1.8
              }}
            >
              From authentication to investment advisory, our team of numismatic experts provides 
              white-glove service for every aspect of coin collecting and investing.
            </Typography>
          </Box>
        </motion.div>

        {/* Stats Section */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {[
            { value: "15,000+", label: "Coins Authenticated" },
            { value: "98%", label: "Client Satisfaction" },
            { value: "25+", label: "Years Experience" },
            { value: "24h", label: "Response Time" },
          ].map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#C6A75E', 0.15),
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '2rem' }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#616161' }}>
                    {stat.label}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Service Categories Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: alpha('#9E9E9E', 0.2), mb: 4 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                color: '#616161',
                fontWeight: 600,
                fontSize: '0.9rem',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
              },
            }}
          >
            <Tab label="All Services" />
            <Tab label="Authentication" />
            <Tab label="Valuation" />
            <Tab label="Advisory" />
          </Tabs>
        </Box>

        {/* All Services Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} lg={4} key={service.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ServiceCard>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Box sx={{ color: 'primary.main' }}>
                          {service.icon}
                        </Box>
                        <Chip 
                          label={service.category}
                          size="small"
                          sx={{ 
                            backgroundColor: alpha('#C6A75E', 0.1),
                            color: 'primary.main',
                            fontWeight: 600,
                          }}
                        />
                      </Box>

                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 2 }}>
                        {service.title}
                      </Typography>

                      <Typography variant="body2" sx={{ color: '#616161', mb: 3, lineHeight: 1.7 }}>
                        {service.description}
                      </Typography>

                      <List sx={{ mb: 3 }}>
                        {service.features.slice(0, 4).map((feature, i) => (
                          <ListItem key={i} sx={{ py: 0.5, px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={feature} 
                              primaryTypographyProps={{ 
                                variant: 'body2', 
                                sx: { color: '#616161' } 
                              }} 
                            />
                          </ListItem>
                        ))}
                      </List>

                      <Divider sx={{ my: 2, borderColor: alpha('#C6A75E', 0.2) }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                            Starting from
                          </Typography>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                            {service.price}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                            Turnaround
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                            {service.turnaround}
                          </Typography>
                        </Box>
                      </Box>

                      <Button
                        variant="contained"
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                        sx={{ 
                          py: 1.5,
                          backgroundColor: 'primary.main',
                          color: '#000000',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        }}
                      >
                        Request Service
                      </Button>
                    </CardContent>
                  </ServiceCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Authentication Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Paper
                  sx={{
                    p: 4,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#C6A75E', 0.15),
                    borderRadius: 2,
                    height: '100%',
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 3 }}>
                    Authentication Services
                  </Typography>
                  
                  <Typography variant="body1" sx={{ color: '#616161', mb: 4, lineHeight: 1.8 }}>
                    Our authentication services ensure the genuineness and grade of your coins through 
                    multiple layers of verification by leading experts and certification services.
                  </Typography>

                  <List>
                    {authenticationServices.map((service, index) => (
                      <React.Fragment key={index}>
                        <ListItem sx={{ px: 0, py: 2 }}>
                          <ListItemIcon>
                            <VerifiedIcon sx={{ color: 'primary.main' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                                  {service.name}
                                </Typography>
                                {service.trusted && (
                                  <Chip 
                                    label="Trusted" 
                                    size="small"
                                    sx={{ backgroundColor: alpha('#C6A75E', 0.1), color: 'primary.main', height: 20 }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={service.description}
                            secondaryTypographyProps={{ sx: { color: '#616161' } }}
                          />
                        </ListItem>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, backgroundColor: alpha('#C6A75E', 0.02) }}>
                          <Typography variant="body2" sx={{ color: '#2D2D2D' }}>
                            Price: {service.price}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#2D2D2D' }}>
                            Time: {service.time}
                          </Typography>
                        </Box>
                        {index < authenticationServices.length - 1 && (
                          <Divider sx={{ borderColor: alpha('#C6A75E', 0.1) }} />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Paper
                  sx={{
                    p: 4,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#C6A75E', 0.15),
                    borderRadius: 2,
                    height: '100%',
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 3 }}>
                    Authentication Process
                  </Typography>

                  <Box sx={{ mb: 4 }}>
                    {[
                      { step: 1, title: "Initial Inspection", description: "Physical examination by our experts" },
                      { step: 2, title: "Weight & Dimensions", description: "Precision measurements against specifications" },
                      { step: 3, title: "Metal Analysis", description: "XRF spectrometry for composition verification" },
                      { step: 4, title: "Die Variety Attribution", description: "Identification of specific mint marks" },
                      { step: 5, title: "Certification", description: "PCGS/NGC submission or in-house certificate" },
                    ].map((item) => (
                      <Box key={item.step} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: alpha('#C6A75E', 0.1),
                            color: 'primary.main',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                          }}
                        >
                          {item.step}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                            {item.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#616161' }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      py: 1.8,
                      backgroundColor: 'primary.main',
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                  >
                    Submit for Authentication
                  </Button>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Valuation Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Paper
                  sx={{
                    p: 4,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#C6A75E', 0.15),
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 3 }}>
                    Valuation Services
                  </Typography>
                  
                  <Typography variant="body1" sx={{ color: '#616161', mb: 3 }}>
                    Accurate, market-based valuations for insurance, estate planning, or sale purposes.
                  </Typography>

                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {[
                      { type: "Insurance", price: "$150", time: "5 days" },
                      { type: "Estate", price: "$250", time: "7 days" },
                      { type: "Fair Market", price: "$200", time: "5 days" },
                      { type: "Portfolio", price: "$500+", time: "10 days" },
                    ].map((item) => (
                      <Grid item xs={6} key={item.type}>
                        <Paper
                          sx={{
                            p: 2,
                            backgroundColor: alpha('#C6A75E', 0.02),
                            border: '1px solid',
                            borderColor: alpha('#C6A75E', 0.1),
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                            {item.type}
                          </Typography>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1.1rem' }}>
                            {item.price}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                            {item.time}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      py: 1.5,
                      backgroundColor: 'primary.main',
                      color: '#000000',
                    }}
                  >
                    Request Valuation
                  </Button>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Paper
                  sx={{
                    p: 4,
                    backgroundColor: alpha('#C6A75E', 0.05),
                    border: '1px solid',
                    borderColor: alpha('#C6A75E', 0.2),
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 3 }}>
                    What's Included
                  </Typography>
                  
                  <List>
                    {[
                      "Comprehensive condition report",
                      "Current market analysis",
                      "Comparable sales data",
                      "Photographic documentation",
                      "Certified appraiser signature",
                      "Digital & printed reports"
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 1, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={item} 
                          primaryTypographyProps={{ sx: { color: '#616161' } }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Advisory Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Paper
                  sx={{
                    p: 4,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#C6A75E', 0.15),
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 3 }}>
                    Investment Advisory
                  </Typography>
                  
                  <Typography variant="body1" sx={{ color: '#616161', mb: 4 }}>
                    Our investment advisors work with collectors and institutions to build, manage, 
                    and optimize valuable numismatic portfolios.
                  </Typography>

                  <Grid container spacing={3}>
                    {[
                      {
                        title: "Portfolio Strategy",
                        description: "Customized acquisition plans based on market trends",
                        icon: <AccountBalanceIcon />,
                      },
                      {
                        title: "Market Analysis",
                        description: "In-depth research on emerging opportunities",
                        icon: <TrendingUpIcon />,
                      },
                      {
                        title: "Collection Management",
                        description: "Ongoing portfolio optimization and rebalancing",
                        icon: <DiamondIcon />,
                      },
                      {
                        title: "Divestment Planning",
                        description: "Strategic exit planning for maximum value",
                        icon: <GavelIcon />,
                      },
                    ].map((item, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Paper
                          sx={{
                            p: 3,
                            backgroundColor: alpha('#C6A75E', 0.02),
                            border: '1px solid',
                            borderColor: alpha('#C6A75E', 0.1),
                            height: '100%',
                          }}
                        >
                          <Box sx={{ color: 'primary.main', mb: 2 }}>{item.icon}</Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D2D2D', mb: 1 }}>
                            {item.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#616161' }}>
                            {item.description}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{ mt: 4, py: 1.8 }}
                  >
                    Schedule Consultation
                  </Button>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={5}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Paper
                  sx={{
                    p: 4,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#C6A75E', 0.15),
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 3 }}>
                    Client Testimonials
                  </Typography>

                  {testimonials.map((testimonial, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Avatar sx={{ bgcolor: alpha('#C6A75E', 0.2), color: 'primary.main' }}>
                          {testimonial.avatar}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                            {testimonial.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                            {testimonial.role}
                          </Typography>
                        </Box>
                      </Box>
                      <Rating value={testimonial.rating} readOnly size="small" sx={{ mb: 1, color: 'primary.main' }} />
                      <Typography variant="body2" sx={{ color: '#616161', fontStyle: 'italic' }}>
                        "{testimonial.comment}"
                      </Typography>
                      {index < testimonials.length - 1 && <Divider sx={{ my: 2, borderColor: alpha('#C6A75E', 0.2) }} />}
                    </Box>
                  ))}
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </TabPanel>
      </Container>
    </Box>
  );
};

export default Services;